import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { usePaywallState, type PaywallSnapshot } from "@/hooks/usePaywallState";

interface TenantSettings {
  automation_mode: string;
  avg_deal_size: number | null;
  booking_link: string | null;
  icp_config: any;
  require_approval_emails: boolean;
  require_approval_replies: boolean;
  revenue_target: number | null;
  close_rate: number | null;
  [key: string]: any;
}

interface TenantContextType {
  tenantId: string | null;
  tenantName: string;
  tenantCreatedAt: string | null;
  userId: string | null;
  plan: string;
  paywall: PaywallSnapshot;
  settings: TenantSettings | null;
  isLoading: boolean;
}

const DEFAULT_PAYWALL: PaywallSnapshot = {
  state: "free",
  tier: "free",
  held_meetings: 0,
  first_held_at: null,
  grace_ends_at: null,
  grace_seconds_remaining: null,
  can_send: true,
  meeting_allowance: 1,
  is_beta: false,
};

const TenantContext = createContext<TenantContextType>({
  tenantId: null,
  tenantName: "Workspace",
  tenantCreatedAt: null,
  userId: null,
  plan: "free",
  paywall: DEFAULT_PAYWALL,
  settings: null,
  isLoading: true,
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const syncTenant = async (nextUserId: string | null) => {
      if (!active) return;

      if (!nextUserId) {
        setUserId(null);
        setTenantId(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setUserId(nextUserId);

      const { data } = await supabase
        .from("tenant_memberships")
        .select("tenant_id")
        .eq("user_id", nextUserId)
        .maybeSingle();

      if (!active) return;

      setTenantId(data?.tenant_id ?? null);
      setIsLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void syncTenant(session?.user?.id ?? null);
    });

    void supabase.auth.getSession().then(({ data: { session } }) => {
      void syncTenant(session?.user?.id ?? null);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // NOTE: Identity linking moved to top-level <IdentityLinker /> in App.tsx
  // so it fires for all authenticated users, not only those who mount this hook.

  // Fetch tenant info from tenants table
  const { data: tenantData } = useQuery({
    queryKey: ["tenant-details", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenants")
        .select("*")
        .eq("id", tenantId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Fetch tenant_settings
  const { data: settingsData } = useQuery({
    queryKey: ["tenant-settings", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenant_settings")
        .select("*")
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Paywall snapshot drives all gating + upgrade UX.
  const { data: paywallData } = usePaywallState(tenantId ?? undefined);

  const value: TenantContextType = {
    tenantId,
    tenantName: tenantData?.name ?? "Workspace",
    tenantCreatedAt: tenantData?.created_at ?? null,
    userId,
    plan: tenantData?.tier ?? "free",
    paywall: paywallData ?? DEFAULT_PAYWALL,
    settings: settingsData ? {
      automation_mode: settingsData.automation_mode,
      avg_deal_size: settingsData.avg_deal_size,
      booking_link: settingsData.booking_link,
      icp_config: settingsData.icp_config,
      require_approval_emails: settingsData.require_approval_emails,
      require_approval_replies: settingsData.require_approval_replies,
      revenue_target: settingsData.revenue_target,
      close_rate: settingsData.close_rate,
    } : null,
    isLoading,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
